import {
    createChart,
    CrosshairMode,
    IChartApi,
    LineStyle,
    LogicalRangeChangeEventHandler,
    MouseEventParams,
    Time,
    TimeRangeChangeEventHandler,
} from 'lightweight-charts';
import { observer } from 'mobx-react-lite';

import { useEffect, useImperativeHandle, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import themeStore from '@client/store/themeStore';

interface StockChartProps {
    className?: string;
    onChartReady?: (chart: IChartApi) => void;
    onVisibleLogicalRangeChange?: LogicalRangeChangeEventHandler;
    onVisibleTimeRangeChange?: TimeRangeChangeEventHandler<Time>;
    onCrosshairMove?: (param: MouseEventParams<Time>) => void;
    ref?: React.Ref<StockChartRef>;
}

export interface StockChartRef {
    getChartApi: () => IChartApi | null;
}

const StockChart = observer(
    ({
        className,
        onChartReady,
        onVisibleLogicalRangeChange,
        onVisibleTimeRangeChange,
        onCrosshairMove,
        ref,
    }: StockChartProps) => {
        const chartElementRef = useRef<HTMLDivElement>(null);
        const chartApiRef = useRef<IChartApi | null>(null);
        const handerRef = useRef<{
            onChartReady?: (chart: IChartApi) => void | Promise<void>;
            onVisibleLogicalRangeChange?: LogicalRangeChangeEventHandler;
            onVisibleTimeRangeChange?: TimeRangeChangeEventHandler<Time>;
            onCrosshairMove?: (param: MouseEventParams<Time>) => void;
        }>({});

        handerRef.current.onChartReady = onChartReady;
        handerRef.current.onVisibleLogicalRangeChange = onVisibleLogicalRangeChange;
        handerRef.current.onVisibleTimeRangeChange = onVisibleTimeRangeChange;
        handerRef.current.onCrosshairMove = onCrosshairMove;

        useEffect(() => {
            if (!chartElementRef.current) {
                return;
            }

            const chart = createChart(chartElementRef.current);

            chartApiRef.current = chart;

            const textColor = themeStore.colors.baseContent;
            const backgroundColor = themeStore.colors.base100;
            // 축의 컬러
            const axisColor = themeStore.opacity(themeStore.colors.baseContent, 60);
            // 그리드 컬러
            const gridLineColor = themeStore.opacity(themeStore.colors.baseContent, 5);
            // 크로스헤어 컬러
            const crosshairLineColor = themeStore.opacity(themeStore.colors.baseContent, 60);

            chart.applyOptions({
                autoSize: true,
                layout: {
                    fontFamily: 'Pretendard Variable',
                    textColor: textColor,
                    background: {
                        color: backgroundColor,
                    },
                },
                grid: {
                    vertLines: {
                        color: gridLineColor,
                    },
                    horzLines: {
                        color: gridLineColor,
                    },
                },
                localization: {
                    dateFormat: 'yyyy-MM-dd',
                },
            });

            chart.priceScale('right').applyOptions({
                borderColor: axisColor,
            });
            chart.priceScale('left').applyOptions({
                borderColor: axisColor,
            });
            chart.timeScale().applyOptions({
                borderColor: axisColor,
            });

            // Customizing the Crosshair
            chart.applyOptions({
                crosshair: {
                    mode: CrosshairMode.Magnet,
                    vertLine: {
                        style: LineStyle.SparseDotted,
                        color: crosshairLineColor,
                    },
                    horzLine: {
                        style: LineStyle.SparseDotted,
                        color: crosshairLineColor,
                    },
                },
            });

            const onVisibleLogicalRangeChange: LogicalRangeChangeEventHandler = (
                visibleLogicalRange,
            ) => {
                handerRef.current.onVisibleLogicalRangeChange?.(visibleLogicalRange);
            };

            const onVisibleTimeRangeChange: TimeRangeChangeEventHandler<Time> = (
                visibleTimeRange,
            ) => {
                handerRef.current.onVisibleTimeRangeChange?.(visibleTimeRange);
            };

            const onCrosshairMove = (param: MouseEventParams<Time>) => {
                handerRef.current.onCrosshairMove?.(param);
            };

            const onWindowResize = () => {
                chart.timeScale().fitContent();
            };

            handerRef.current.onChartReady?.(chart);

            chart.timeScale().subscribeVisibleLogicalRangeChange(onVisibleLogicalRangeChange);
            chart.timeScale().subscribeVisibleTimeRangeChange(onVisibleTimeRangeChange);
            chart.subscribeCrosshairMove(onCrosshairMove);
            window.addEventListener('resize', onWindowResize);

            return () => {
                chart.timeScale().unsubscribeVisibleLogicalRangeChange(onVisibleLogicalRangeChange);
                chart.timeScale().unsubscribeVisibleTimeRangeChange(onVisibleTimeRangeChange);
                chart.unsubscribeCrosshairMove(onCrosshairMove);
                chart.remove();
                window.removeEventListener('resize', onWindowResize);
            };
        }, [themeStore.themeToRender]);

        useImperativeHandle(ref, () => ({
            getChartApi: () => chartApiRef.current,
        }));

        return (
            <div
                data-ignore-carousel={true}
                data-ignore-pull-to-refresh={true}
                ref={chartElementRef}
                // 기본 클래스
                // - 기본 높이 적용
                // - PullToRefresh 가 되지 않게 touch-none 적용
                // - 숫자 고정폭 적용
                className={twMerge('h-60 w-full touch-none tabular-nums', className)}
            ></div>
        );
    },
);

export default StockChart;
